import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ScrollText } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { dismissTermsBanner, isTermsBannerDismissed } from '../../utils/localStorageManager.js';
import TermsDialog from './TermsDialog.tsx';

const TermsBanner = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (!isTermsBannerDismissed()) {
      setShowBanner(true);
    }
  }, []);

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleDismissBanner = () => {
    dismissTermsBanner();
    setShowBanner(false);
  };

  return (
    <>
      <AnimatePresence>
        {showBanner && (
          <>
            {/* Mobile: Bubble mode */}
            <div className="block sm:hidden">
              {!isExpanded ? (
                <motion.button
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  onClick={() => setIsExpanded(true)}
                  className="fixed bottom-4 left-4 z-[1300] w-12 h-12 rounded-full shadow-lg
                             bg-slate-600 dark:bg-slate-700 text-white flex items-center justify-center
                             hover:scale-110 active:scale-95 transition-transform"
                  aria-label="View Terms and Conditions"
                >
                  <ScrollText className="h-5 w-5" />
                </motion.button>
              ) : (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="fixed bottom-4 right-4 left-4 z-[1300] bg-card border border-border
                             rounded-lg p-3 shadow-lg flex items-center gap-3"
                >
                  <p className="text-xs text-muted-foreground flex-1">
                    If you use our app, it is taken that you comply with our{' '}
                    <button
                      onClick={handleOpenDialog}
                      className="text-primary hover:underline font-medium"
                    >
                      terms and conditions
                    </button>
                    . You can find terms and conditions again in your user profile, if you wanna review them at later stage.
                  </p>
                  <div className="flex gap-1 shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setIsExpanded(false)}
                      className="h-6 w-6"
                      aria-label="Collapse"
                    >
                      <ScrollText className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handleDismissBanner}
                      className="h-6 w-6"
                      aria-label="Dismiss"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </motion.div>
              )}
            </div>

            {/* Desktop: Full-width banner */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 50 }}
              transition={{ duration: 0.5 }}
              className="hidden sm:flex fixed bottom-0 left-0 right-0 w-full bg-card border-t border-border p-3 items-center justify-center gap-3 text-xs z-[1300]"
            >
              <p className="text-center text-sm text-muted-foreground">
                If you use our app, it is taken that you comply with our{' '}
                <button
                  onClick={handleOpenDialog}
                  className="text-primary hover:underline font-medium"
                >
                  terms and conditions
                </button>
                . You can find terms and conditions again in your user profile, if you wanna review them at later stage.
              </p>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleDismissBanner}
                className="h-6 w-6 shrink-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Reusable TermsDialog */}
      <TermsDialog open={openDialog} onClose={handleCloseDialog} />
    </>
  );
};

export default TermsBanner;
